// src/utils/statements.js
//
// Issued driver and owner-operator statements.
//
// The StatementsPage has always been able to calculate a statement and print it.
// What it could not do was *issue* one: nothing was saved, so there was no record
// of what a driver had been told they were owed, nothing to re-open next month,
// and nothing to notify anyone about.
//
// The single most important property here: an issued statement is a FROZEN
// SNAPSHOT. The figures are copied in at the moment of issue and never
// recalculated. If a statement re-derived itself from live data, then editing a
// load or adding a bonus weeks later would silently change what a driver was
// already paid against — the numbers on screen would stop matching the numbers
// that were agreed, with nothing to show that they had moved. A statement is a
// record of a decision, not a view onto current data.
//
// That is also why there is no edit. A statement that is wrong gets voided, with
// a reason, and a new one issued in its place; the void stays visible. Accounting
// records that can be quietly rewritten are worse than no records at all.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const STATEMENT_TYPE_DRIVER = 'driver';
export const STATEMENT_TYPE_OWNER = 'owner';

export const STATEMENT_STATUS_ISSUED = 'issued';
export const STATEMENT_STATUS_VOIDED = 'voided';

/** Roles permitted to issue and void. Mirrors canGenerate on the page. */
export const STATEMENT_ROLES = ['Super Admin', 'Main Admin', 'Admin', 'Accountant'];

const rolesOf = (user) => {
  if (!user) return [];
  if (Array.isArray(user.role)) return user.role.filter(Boolean);
  return user.role ? [user.role] : [];
};

export const canIssueStatements = (user) =>
  rolesOf(user).some(role => STATEMENT_ROLES.includes(role));

/**
 * The next statement number for a tenant, as a transaction.
 *
 * A counter rather than a timestamp or a random suffix because these are
 * financial documents: people refer to them by number, and two statements
 * sharing one is a real problem to untangle. The transaction is what makes two
 * people pressing Issue at the same moment safe.
 */
async function nextStatementNumber(tenantId) {
  const counterRef = doc(db, 'statementCounters', tenantId);

  const next = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const current = snap.exists() ? Number(snap.data()?.next) || 0 : 0;
    const value = current + 1;
    transaction.set(
      counterRef,
      { next: value, tenantId, updatedAt: serverTimestamp() },
      { merge: true }
    );
    return value;
  });

  const year = new Date().getFullYear();
  return `ST-${year}-${String(next).padStart(4, '0')}`;
}

/** Numbers arrive from the page in mixed shapes; store them as numbers. */
const num = (value) => {
  const n = Number(String(value ?? 0).replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Dates may be Date, Firestore Timestamp, or a string. Store something stable. */
const toIso = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

/**
 * Line items, flattened to plain values.
 *
 * The page builds these from live documents, which carry Timestamps and nested
 * references Firestore will not accept inside an array. Flattening here keeps the
 * saved statement readable on its own, without needing the original loads to
 * still exist or still say the same thing.
 */
const freezeLines = (lines = []) =>
  (Array.isArray(lines) ? lines : []).map(line => ({
    description: line.description ?? line.label ?? '',
    type: line.type ?? null,
    loadId: line.loadId ?? line.load_id ?? null,
    date: toIso(line.date),
    miles: line.miles != null ? num(line.miles) : null,
    gross: line.gross != null ? num(line.gross) : null,
    amount: num(line.amount),
  }));

/**
 * Save a statement.
 *
 * @param {Object} params
 * @param {Object} params.statementData  exactly what the page already computed
 * @param {string} params.type           STATEMENT_TYPE_DRIVER | STATEMENT_TYPE_OWNER
 * @param {Object} params.loggedInUser
 * @param {string} params.driverId
 * @param {string} params.truckId
 * @param {string} params.periodStart    yyyy-mm-dd as the page holds it
 * @param {string} params.periodEnd
 * @returns {Promise<{id: string, statementNumber: string}>}
 */
export async function issueStatement({
  statementData,
  type,
  loggedInUser,
  driverId,
  truckId,
  periodStart,
  periodEnd,
}) {
  if (!canIssueStatements(loggedInUser)) {
    throw new Error('You do not have permission to issue statements.');
  }
  const tenantId = loggedInUser?.tenantId;
  if (!tenantId) throw new Error('No tenant on the signed-in user.');
  if (!statementData) throw new Error('Nothing to issue.');

  const statementNumber = await nextStatementNumber(tenantId);
  const calc = statementData.calculations || {};
  const totals = statementData.totals || {};

  const record = {
    statementNumber,
    type,
    status: STATEMENT_STATUS_ISSUED,

    tenantId,
    companyId: statementData.company?.id || null,
    companyName: statementData.company?.name || '',

    driverId: driverId || statementData.driver?.id || null,
    driverName: statementData.driver?.name || statementData.driver?.email || '',
    truckId: truckId || statementData.truck?.id || null,
    truckNumber: statementData.truckNumber || '',

    periodStart: periodStart || null,
    periodEnd: periodEnd || null,
    periodLabel: statementData.period || '',
    timezone: statementData.timezone || null,

    // The frozen figures. Everything a reader needs is here; nothing is looked
    // up again later.
    lines: freezeLines(statementData.loads),
    deductions: freezeLines(statementData.deductions),
    bonuses: freezeLines(statementData.bonuses),
    totals: {
      loadCount: num(totals.loadCount),
      totalGross: num(totals.totalGross),
      totalMiles: num(totals.totalMiles),
      grossEarnings: num(totals.grossEarnings),
    },
    calculations: {
      grossEarnings: num(calc.grossEarnings),
      totalBonuses: num(calc.totalBonuses),
      totalDeductions: num(calc.totalDeductions),
      netPay: num(calc.netPay),
    },
    compensation: {
      type: statementData.compensationDetails?.type || null,
      rate: statementData.compensationDetails?.rate || null,
    },

    issuedAt: serverTimestamp(),
    issuedBy: loggedInUser?.uid || null,
    issuedByEmail: loggedInUser?.email || null,
    issuedByName: loggedInUser?.displayName || loggedInUser?.email || '',
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'statements'), record);
  return { id: ref.id, statementNumber };
}

/**
 * Void a statement. Never deletes: the record and its number stay, marked, so the
 * history of what a driver was told remains readable.
 */
export async function voidStatement(statementId, reason, loggedInUser) {
  if (!canIssueStatements(loggedInUser)) {
    throw new Error('You do not have permission to void statements.');
  }
  if (!statementId) throw new Error('No statement given.');

  await updateDoc(doc(db, 'statements', statementId), {
    status: STATEMENT_STATUS_VOIDED,
    voidReason: (reason || '').trim() || 'No reason given',
    voidedAt: serverTimestamp(),
    voidedBy: loggedInUser?.uid || null,
    voidedByEmail: loggedInUser?.email || null,
  });
}
